import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { bookingApi } from '../utils/bookingApi';
import { archiveCreditNote } from '../utils/archiveCredit';
import { formatDate } from '../utils/dates';


const Gymnasts = () => {
  const [gymnasts, setGymnasts] = useState([]);
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [archivingGymnast, setArchivingGymnast] = useState(null);
  const [archiveReason, setArchiveReason] = useState('');
  const [deletingGymnast, setDeletingGymnast] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showQuickNav, setShowQuickNav] = useState(false);
  const [sessionGymnasts, setSessionGymnasts] = useState(new Set());
  const [todaySessions, setTodaySessions] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  // Selecting a session filters the list immediately — there is no separate on/off
  // toggle, so this can never disagree with what's actually selected.
  const showSessionOnly = !!selectedSessionId;
  const [sessionLoading, setSessionLoading] = useState(false);
  const [savedSortBy, setSortBy] = useState('name'); // 'name', 'level', 'recent', 'age'
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;
  // Restoring stays with Club admins (the server asks for one); today's
  // sessions filter reads registers
  const { isClubAdmin, can } = useAuth();
  const seesRegisters = can('registers');
  // Dates of birth come only to staff with Members or First aid; staff here
  // for skill tracking alone get the list without them
  const seesBirthDates = can('members') || can('firstAid');
  const sortBy = !seesBirthDates && savedSortBy === 'age' ? 'name' : savedSortBy;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Helper function to check if a level is a side-track
  const isSideTrack = (identifier) => {
    return /^\d+[a-z]$/.test(identifier);
  };

  // Load session and preferences from localStorage on component mount
  useEffect(() => {
    const storedSortBy = localStorage.getItem('gymnastSortBy');
    if (storedSortBy) setSortBy(storedSortBy);

    const savedShowArchived = localStorage.getItem('gymnastShowArchived');
    if (savedShowArchived) setShowArchived(savedShowArchived === 'true');

    const savedSearchTerm = localStorage.getItem('gymnastSearchTerm');
    if (savedSearchTerm) setSearchTerm(savedSearchTerm);
  }, []);

  // Save preferences to localStorage when they change
  useEffect(() => {
    localStorage.setItem('gymnastSortBy', savedSortBy);
  }, [savedSortBy]);

  useEffect(() => {
    localStorage.setItem('gymnastShowArchived', showArchived.toString());
  }, [showArchived]);

  useEffect(() => {
    localStorage.setItem('gymnastSearchTerm', searchTerm);
  }, [searchTerm]);

  useEffect(() => { setPage(1); }, [searchTerm, showSessionOnly]);

  useEffect(() => {
    if (!seesRegisters) return;
    const now = new Date();
    bookingApi.getSessions(now.getFullYear(), now.getMonth() + 1)
      .then(res => {
        // Compare local calendar date against the session date's UTC components (not
        // its ISO string) — session dates are stored as UTC midnight for the intended
        // calendar day, so re-deriving "today" via toISOString() shifts by the
        // viewer's UTC offset and drops today's evening sessions from the list.
        const sessions = res.data.filter(s => {
          const d = new Date(s.date);
          return d.getUTCFullYear() === now.getFullYear() &&
            d.getUTCMonth() === now.getMonth() &&
            d.getUTCDate() === now.getDate();
        });
        setTodaySessions(sessions);

        // Only restore a session filter saved from a previous visit if it's still one
        // of today's sessions — otherwise a filter left over from a prior day silently
        // reapplies and shows that day's gymnasts under today's page.
        const savedSessionFilter = localStorage.getItem('gymnastSessionFilter');
        if (!savedSessionFilter) return;
        if (!sessions.some(s => s.id === savedSessionFilter)) {
          localStorage.removeItem('gymnastSessionFilter');
          return;
        }
        setSelectedSessionId(savedSessionFilter);
        setSessionLoading(true);
        bookingApi.getAttendance(savedSessionFilter)
          .then(attRes => {
            setSessionGymnasts(new Set(attRes.data.attendees.map(a => a.gymnastId)));
          })
          .catch(() => {
            localStorage.removeItem('gymnastSessionFilter');
            setSelectedSessionId(null);
          })
          .finally(() => setSessionLoading(false));
      })
      .catch(() => {
        // Silently fail — dropdown renders as disabled
      });
  }, [seesRegisters]);

  const handleSessionSelect = async (sessionId) => {
    if (!sessionId) {
      setSelectedSessionId(null);
      setSessionGymnasts(new Set());
      localStorage.removeItem('gymnastSessionFilter');
      return;
    }
    setSelectedSessionId(sessionId);
    setSessionLoading(true);
    try {
      const res = await bookingApi.getAttendance(sessionId);
      setSessionGymnasts(new Set(res.data.attendees.map(a => a.gymnastId)));
    } catch (err) {
      setError('Failed to load session attendees. Please try again.');
      setSelectedSessionId(null);
      // Clear the stale set instead of leaving it — otherwise the dropdown reads "No
      // session" while the list is still filtered to the previous session's gymnasts.
      setSessionGymnasts(new Set());
      localStorage.removeItem('gymnastSessionFilter');
    } finally {
      setSessionLoading(false);
    }
  };

  // Helper function to determine the gymnast's current working level number
  const getCurrentLevel = (gymnast, levels) => {
    if (!gymnast || !levels.length) return null;

    // Get all completed main track levels (ignore side tracks)
    const completedMainTrackLevels = gymnast.levelProgress
      .filter(lp => lp.status === 'COMPLETED')
      .map(lp => lp.level)
      .filter(level => !isSideTrack(level.identifier)) // Only main track levels
      .map(level => parseInt(level.identifier))
      .filter(Number.isFinite) // ignore levels whose code isn't a number
      .sort((a, b) => a - b);

    // Find the next main track level to work on
    let nextLevelNumber = 1;
    if (completedMainTrackLevels.length > 0) {
      const highestCompleted = Math.max(...completedMainTrackLevels);
      nextLevelNumber = highestCompleted + 1;
    }

    // Find the actual level object
    return levels.find(level => 
      !isSideTrack(level.identifier) && parseInt(level.identifier) === nextLevelNumber
    ) || null;
  };

  // Remove unused function getHighestCompletedLevelForDashboard

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [gymnastsResponse, levelsResponse] = await Promise.all([
          axios.get(`/api/gymnasts${showArchived ? '?includeArchived=true' : ''}`),
          axios.get('/api/levels')
        ]);
        setGymnasts(gymnastsResponse.data);
        setLevels(levelsResponse.data);
      } catch (error) {
        console.error('Failed to fetch data:', error);
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [showArchived]);

  // Handle ?session=<instanceId> deep link from "Track these gymnasts" button.
  // Depend on the param value (not just mount) — otherwise clicking "Track
  // these gymnasts" a second time while already on /gymnasts updates the
  // URL but the effect never re-fires, so the wrong session stays loaded.
  // A `cancelled` flag also discards stale fetch results when the user
  // switches sessions rapidly.
  const sessionParamFromUrl = searchParams.get('session');
  useEffect(() => {
    if (!sessionParamFromUrl) return;
    let cancelled = false;

    const next = new URLSearchParams(searchParams);
    next.delete('session');
    setSearchParams(next, { replace: true });

    setSelectedSessionId(sessionParamFromUrl);
    setSessionLoading(true);
    localStorage.setItem('gymnastSessionFilter', sessionParamFromUrl);

    bookingApi.getAttendance(sessionParamFromUrl)
      .then(res => {
        if (cancelled) return;
        setSessionGymnasts(new Set(res.data.attendees.map(a => a.gymnastId)));
      })
      .catch(() => {
        if (cancelled) return;
        setError('Failed to load session attendees. Please try again.');
        setSelectedSessionId(null);
        setSessionGymnasts(new Set());
        localStorage.removeItem('gymnastSessionFilter');
      })
      .finally(() => { if (!cancelled) setSessionLoading(false); });

    return () => { cancelled = true; };
  }, [sessionParamFromUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check for URL parameters to highlight specific gymnast or apply filters
  useEffect(() => {
    const highlightId = searchParams.get('highlight');
    if (highlightId && gymnasts.length > 0) {
      const targetGymnast = gymnasts.find(g => g.id === highlightId);
      if (targetGymnast) {
        setSearchTerm(`${targetGymnast.firstName} ${targetGymnast.lastName}`);
      }
    }
  }, [searchParams, gymnasts]);

  const handleArchiveClick = (e, gymnast) => {
    if (e) e.stopPropagation();
    setArchivingGymnast(gymnast);
    setArchiveReason('');
  };

  const handleArchiveSubmit = async () => {
    try {
      const res = await axios.patch(`/api/gymnasts/${archivingGymnast.id}/archive`, {
        reason: archiveReason
      });
      
      setSuccess(`${archivingGymnast.firstName} ${archivingGymnast.lastName} has been archived.${archiveCreditNote(res?.data?.cascade)}`);
      setArchivingGymnast(null);
      setArchiveReason('');
      
      // Refresh gymnasts list
      const response = await axios.get(`/api/gymnasts${showArchived ? '?includeArchived=true' : ''}`);
      setGymnasts(response.data);
    } catch (error) {
      console.error('Archive gymnast error:', error);
      setError(error.response?.data?.error || 'Failed to archive gymnast');
    }
  };

  const handleRestoreClick = async (e, gymnast) => {
    e.stopPropagation();
    try {
      await axios.patch(`/api/gymnasts/${gymnast.id}/restore`);
      
      setSuccess(`${gymnast.firstName} ${gymnast.lastName} has been restored`);
      
      // Refresh gymnasts list
      const response = await axios.get(`/api/gymnasts${showArchived ? '?includeArchived=true' : ''}`);
      setGymnasts(response.data);
    } catch (error) {
      console.error('Restore gymnast error:', error);
      setError(error.response?.data?.error || 'Failed to restore gymnast');
    }
  };

  const handleDeleteClick = (e, gymnast) => {
    e.stopPropagation();
    setDeletingGymnast(gymnast);
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(`/api/gymnasts/${deletingGymnast.id}`, {
        data: { confirmDelete: true }
      });
      
      setSuccess(`${deletingGymnast.firstName} ${deletingGymnast.lastName} has been deleted permanently`);
      setDeletingGymnast(null);
      
      // Refresh gymnasts list
      const response = await axios.get(`/api/gymnasts${showArchived ? '?includeArchived=true' : ''}`);
      setGymnasts(response.data);
    } catch (error) {
      console.error('Delete gymnast error:', error);
      setError(error.response?.data?.error || 'Failed to delete gymnast');
      setDeletingGymnast(null);
    }
  };

  const handleRowClick = (gymnast) => {
    const currentLevel = getCurrentLevel(gymnast, levels);
    if (currentLevel) {
      navigate(`/progress/${gymnast.id}#level-${currentLevel.id}`);
    } else {
      navigate(`/progress/${gymnast.id}`);
    }
  };


  // Filter gymnasts based on search term, URL parameters, and session mode
  const filteredGymnasts = gymnasts.filter(gymnast => {
    const search = searchTerm.toLowerCase();
    const fullName = `${gymnast.firstName} ${gymnast.lastName}`.toLowerCase();
    const guardianNames = gymnast.guardians.map(g => `${g.firstName} ${g.lastName}`.toLowerCase()).join(' ');
    
    // Check search term
    const matchesSearch = !search || fullName.includes(search) || guardianNames.includes(search);
    
    // Check level filter (match what's displayed as "Current Level")
    const levelFilter = searchParams.get('level');
    const matchesLevel = !levelFilter || (() => {
      const currentLevel = getCurrentLevel(gymnast, levels);
      return currentLevel && currentLevel.identifier === levelFilter;
    })();
    
    // Check session filter
    const matchesSession = !showSessionOnly || sessionGymnasts.has(gymnast.id);
    
    return matchesSearch && matchesLevel && matchesSession;
  });

  // Sort gymnasts based on selected sort option
  const sortGymnasts = (gymnasts) => {
    return [...gymnasts].sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
        
        case 'level':
          const levelA = getCurrentLevel(a, levels);
          const levelB = getCurrentLevel(b, levels);
          if (!levelA && !levelB) return 0;
          if (!levelA) return 1;
          if (!levelB) return -1;
          return levelA.identifier.localeCompare(levelB.identifier);
        
        case 'recent':
          const dateA = new Date(a.updatedAt || a.createdAt);
          const dateB = new Date(b.updatedAt || b.createdAt);
          return dateB - dateA;
        
        case 'age':
          const ageA = a.dateOfBirth ? new Date().getFullYear() - new Date(a.dateOfBirth).getFullYear() : 0;
          const ageB = b.dateOfBirth ? new Date().getFullYear() - new Date(b.dateOfBirth).getFullYear() : 0;
          return ageA - ageB; // Youngest first
        
        default:
          return 0;
      }
    });
  };

  // Separate active and archived gymnasts, then sort them
  const activeGymnasts = sortGymnasts(filteredGymnasts.filter(g => !g.isArchived));
  const archivedGymnasts = sortGymnasts(filteredGymnasts.filter(g => g.isArchived));

  const totalPages = Math.max(1, Math.ceil(activeGymnasts.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paginatedActive = activeGymnasts.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // Settings inside the collapsed Filters panel that differ from the default
  const hiddenFilterCount = [
    searchParams.get('level'),
    sortBy !== 'name',
    showArchived,
  ].filter(Boolean).length;

  const sessionLabel = (s) => `${s.startTime}–${s.endTime} · ${s.type.charAt(0) + s.type.slice(1).toLowerCase()}`;

  // Compact level pill for the mobile rows: the level being worked on ("L7"; a
  // new gymnast is on L1). No current level means every main level is done.
  const levelPill = (gymnast) => {
    const currentLevel = getCurrentLevel(gymnast, levels);
    if (currentLevel) {
      return (
        <span className="gymnast-row__level" title={`Level ${currentLevel.identifier}: ${currentLevel.name}`}>
          L{currentLevel.identifier}
        </span>
      );
    }
    const completedAny = gymnast.levelProgress?.some(lp => lp.status === 'COMPLETED');
    return (
      <span className="gymnast-row__level gymnast-row__level--none" title={completedAny ? 'All levels completed' : 'No levels set up'}>
        {completedAny ? 'Done' : '–'}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="loading mobile-loading">
        <div className="spinner"></div>
        <p className="loading-text">Loading gymnasts...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error">
        {error}
      </div>
    );
  }

  return (
    <div>
      {success && (
        <div className="alert alert-success">
          {success}
          <button
            className="btn btn-xs btn-outline alert-dismiss-btn"
            onClick={() => setSuccess(null)}
          >
            ×
          </button>
        </div>
      )}

      {/* Archive Modal */}
      {archivingGymnast && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Archive Gymnast</h3>
            <p>Are you sure you want to archive <strong>{archivingGymnast.firstName} {archivingGymnast.lastName}</strong>?</p>
            <p>Archived gymnasts will be hidden from the main list but their progress data will be preserved.</p>
            <p>Their memberships, standing slots and future bookings are cancelled. Paid future bookings are credited to the family.</p>
            
            <div className="form-group">
              <label>Reason for archiving (optional):</label>
              <textarea
                value={archiveReason}
                onChange={(e) => setArchiveReason(e.target.value)}
                placeholder="e.g., Left club, moved away, etc."
                className="form-control"
                rows="3"
              />
            </div>
            
            <div className="modal-actions">
              <button 
                onClick={() => setArchivingGymnast(null)}
                className="btn btn-outline"
              >
                Cancel
              </button>
              <button 
                onClick={handleArchiveSubmit}
                className="btn btn-warning"
              >
                Archive Gymnast
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deletingGymnast && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Delete Gymnast</h3>
            <p>Are you sure you want to <strong>permanently delete</strong> <strong>{deletingGymnast.firstName} {deletingGymnast.lastName}</strong>?</p>
            <div className="alert alert-error">
              <strong>Warning:</strong> This action cannot be undone. All progress data will be lost.
              <br />
              Consider archiving instead to preserve historical data.
            </div>
            
            <div className="modal-actions">
              <button 
                onClick={() => setDeletingGymnast(null)}
                className="btn btn-outline"
              >
                Cancel
              </button>
              <button 
                onClick={() => {
                  setDeletingGymnast(null);
                  handleArchiveClick(null, deletingGymnast);
                }}
                className="btn btn-warning"
              >
                Archive Instead
              </button>
              <button 
                onClick={handleDeleteConfirm}
                className="btn btn-danger"
              >
                Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search, today's sessions (always visible) and the collapsible Filters panel */}
      <div className="card gymnasts-filters">
        <div className="gymnasts-filters__top">
          <input
            type="search"
            placeholder="Search gymnasts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="form-control gymnasts-search-input"
          />
          <button
            type="button"
            className={`gymnasts-filters__toggle${showFilters ? ' open' : ''}`}
            aria-expanded={showFilters}
            aria-controls="gymnasts-filters-panel"
            onClick={() => setShowFilters(v => !v)}
          >
            Filters
            {hiddenFilterCount > 0 && <span className="gymnasts-filters__count">{hiddenFilterCount}</span>}
            <span aria-hidden="true" className="gymnasts-filters__chevron">▾</span>
          </button>
        </div>

        {seesRegisters && (
          <div className="gymnasts-sessions" role="group" aria-label="Today's sessions">
            <span className="gymnasts-sessions__label">Today's sessions</span>
            {todaySessions.length === 0 ? (
              <span className="gymnasts-sessions__empty">No sessions today</span>
            ) : (
              <div className="gymnasts-sessions__chips">
                <button
                  type="button"
                  className={`gymnasts-chip${!selectedSessionId ? ' active' : ''}`}
                  aria-pressed={!selectedSessionId}
                  onClick={() => handleSessionSelect(null)}
                  disabled={sessionLoading}
                >
                  All gymnasts
                </button>
                {todaySessions.map(s => {
                  const selected = s.id === selectedSessionId;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      className={`gymnasts-chip${selected ? ' active' : ''}`}
                      aria-pressed={selected}
                      onClick={() => handleSessionSelect(selected ? null : s.id)}
                      disabled={sessionLoading}
                    >
                      {sessionLabel(s)}
                      {selected && (
                        <span className="gymnasts-chip__count">{sessionLoading ? '…' : sessionGymnasts.size}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {showFilters && (
          <div id="gymnasts-filters-panel" className="gymnasts-filters__panel">
            <label className="gymnasts-filters__field">
              <span>Sort by</span>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="form-control">
                <option value="name">Name (A–Z)</option>
                <option value="recent">Most recent</option>
                <option value="level">Level</option>
                {seesBirthDates && <option value="age">Age (youngest first)</option>}
              </select>
            </label>

            <label className="gymnasts-filters__field">
              <span>Level</span>
              <select
                value={searchParams.get('level') || ''}
                onChange={(e) => {
                  const newParams = new URLSearchParams(searchParams);
                  if (e.target.value) newParams.set('level', e.target.value);
                  else newParams.delete('level');
                  setSearchParams(newParams);
                }}
                className="form-control"
              >
                <option value="">All levels</option>
                {levels
                  .filter(level => !isSideTrack(level.identifier))
                  .sort((a, b) => parseInt(a.identifier) - parseInt(b.identifier))
                  .map(level => (
                    <option key={level.id} value={level.identifier}>
                      Level {level.identifier}
                    </option>
                  ))}
              </select>
            </label>

            {isClubAdmin && (
              <label className="gymnasts-archive-label">
                <input
                  type="checkbox"
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                />
                <span>Show archived</span>
              </label>
            )}
          </div>
        )}

        {/* Active Level filter, visible even with the panel closed */}
        {searchParams.get('level') && (
          <div className="gymnasts-active-filters">
            <span className="gymnasts-filter-label">Active filters:</span>
            {searchParams.get('level') && (
              <span className="badge badge-info gymnasts-filter-chip">
                Level {searchParams.get('level')}
                <button onClick={() => { const p = new URLSearchParams(searchParams); p.delete('level'); setSearchParams(p); }} className="gymnasts-filter-chip-remove">×</button>
              </span>
            )}
            <button className="btn btn-xs btn-outline" onClick={() => setSearchParams(new URLSearchParams())}>Clear</button>
          </div>
        )}
      </div>

      {/* Active Gymnasts */}
      {activeGymnasts.length === 0 && !showArchived ? (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">No Active Gymnasts Found</h3>
          </div>
          <div>
            {gymnasts.length === 0 ? (
              <>
                <p>No gymnasts have been added to your club yet.</p>
              </>
            ) : (
              <>
                <p>No active gymnasts match your search criteria.</p>
                {(searchTerm || searchParams.get('level') || showSessionOnly) && (
                  <div style={{ marginTop: '1rem' }}>
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={() => {
                        setSearchTerm('');
                        setSelectedSessionId(null);
                        setSessionGymnasts(new Set());
                        localStorage.removeItem('gymnastSessionFilter');
                        setSearchParams(new URLSearchParams());
                      }}
                    >
                      Clear All Filters
                    </button>
                  </div>
                )}
              </>
)}
          </div>
        </div>
      ) : (
        <>
          {/* Active Gymnasts Table */}
          {activeGymnasts.length > 0 && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">
                  Active Gymnasts ({activeGymnasts.length})
                </h3>
              </div>
              
              {/* Desktop Table */}
              <table className="table">
                <thead>
                  <tr>
                    <th>Name</th>
                    {seesBirthDates && <th>Date of Birth</th>}
                    <th>Current Level</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedActive.map(gymnast => (
                    <tr
                      key={gymnast.id}
                      onClick={() => handleRowClick(gymnast)}
                      style={{ cursor: 'pointer' }}
                      title="Click to view progress"
                    >
                      <td>
                        <strong>{gymnast.firstName} {gymnast.lastName}</strong>
                      </td>
                      {seesBirthDates && (
                        <td>
                          {gymnast.dateOfBirth
                            ? formatDate(gymnast.dateOfBirth)
                            : 'Not specified'
                          }
                        </td>
                      )}
                      <td>
                        {(() => {
                          const currentLevel = getCurrentLevel(gymnast, levels);
                          return currentLevel ? (
                            <span className="level-badge">
                              Level {currentLevel.identifier}: {currentLevel.name}
                            </span>
                          ) : (
                            <span className="badge badge-secondary">
                              Not started
                            </span>
                          );
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Mobile Cards */}
              <div className="mobile-table-cards">
                {paginatedActive.map(gymnast => (
                  <button
                    type="button"
                    key={gymnast.id}
                    className="gymnast-row"
                    onClick={() => handleRowClick(gymnast)}
                  >
                    <span className="gymnast-row__name">{gymnast.firstName} {gymnast.lastName}</span>
                    <span className="gymnast-row__meta">{levelPill(gymnast)}</span>
                  </button>
                ))}
              </div>

              {/* Quick Navigation for Mobile */}
              {activeGymnasts.length > 1 && (
                <div className="mobile-quick-nav">
                  <button 
                    className="mobile-quick-nav-toggle"
                    onClick={() => setShowQuickNav(!showQuickNav)}
                  >
                    <span>Quick Jump</span>
                    <span className={`quick-nav-arrow ${showQuickNav ? 'open' : ''}`}>▼</span>
                  </button>
                  
                  {showQuickNav && (
                    <div className="mobile-quick-nav-list">
                      {activeGymnasts.map((gymnast, index) => (
                        <button
                          key={gymnast.id}
                          className="mobile-quick-nav-item"
                          onClick={() => {
                            handleRowClick(gymnast);
                            setShowQuickNav(false);
                          }}
                        >
                          <span className="quick-nav-name">
                            {gymnast.firstName} {gymnast.lastName}
                          </span>
                          <span className="quick-nav-level">
                            {(() => {
                              const currentLevel = getCurrentLevel(gymnast, levels);
                              return currentLevel ? `Level ${currentLevel.identifier}` : 'Not started';
                            })()}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            {totalPages > 1 && (
              <div className="gymnasts-pagination">
                <button
                  className="btn btn-sm btn-outline"
                  disabled={safePage <= 1}
                  onClick={() => setPage(p => p - 1)}
                >Prev</button>
                <span className="gymnasts-pagination-info">Page {safePage} of {totalPages}</span>
                <button
                  className="btn btn-sm btn-outline"
                  disabled={safePage >= totalPages}
                  onClick={() => setPage(p => p + 1)}
                >Next</button>
              </div>
            )}
            </div>
          )}

          {/* Archived Gymnasts Table */}
          {showArchived && archivedGymnasts.length > 0 && (
            <div className="card" style={{ marginTop: '1rem' }}>
              <div className="card-header">
                <h3 className="card-title">
                  Archived Gymnasts ({archivedGymnasts.length})
                </h3>
              </div>
              
              {/* Desktop Table */}
              <table className="table">
                <thead>
                  <tr>
                    <th>Name</th>
                    {seesBirthDates && <th>Date of Birth</th>}
                    <th>Archived Date</th>
                    <th>Reason</th>
                    <th>Guardians</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {archivedGymnasts.map(gymnast => (
                    <tr 
                      key={gymnast.id} 
                      onClick={() => handleRowClick(gymnast)}
                      style={{ cursor: 'pointer', opacity: 0.7 }}
                      title="Click to view progress"
                    >
                      <td>
                        <strong>{gymnast.firstName} {gymnast.lastName}</strong>
                        <span className="badge badge-secondary" style={{ marginLeft: '0.5rem' }}>
                          Archived
                        </span>
                      </td>
                      {seesBirthDates && (
                        <td>
                          {gymnast.dateOfBirth
                            ? formatDate(gymnast.dateOfBirth)
                            : 'Not specified'
                          }
                        </td>
                      )}
                      <td>
                        {gymnast.archivedAt 
                          ? formatDate(gymnast.archivedAt)
                          : 'Unknown'
                        }
                      </td>
                      <td>
                        {gymnast.archivedReason || 'No reason specified'}
                      </td>
                      <td>
                        {gymnast.guardians.length > 0 ? (
                          <div>
                            {gymnast.guardians.map(guardian => (
                              <div key={guardian.id}>
                                <Link 
                                  to={`/adults?highlight=${guardian.id}`}
                                  className="text-link"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <small>{guardian.firstName} {guardian.lastName}</small>
                                </Link>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-muted">No guardians</span>
                        )}
                      </td>
                      <td>
                        {isClubAdmin && (
                          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <button 
                              onClick={(e) => handleRestoreClick(e, gymnast)}
                              className="btn btn-sm btn-success"
                              title="Restore gymnast"
                            >
                              Restore
                            </button>
                            <button 
                              onClick={(e) => handleDeleteClick(e, gymnast)}
                              className="btn btn-sm btn-danger"
                              title="Delete gymnast permanently"
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Mobile Cards */}
              <div className="mobile-table-cards">
                {archivedGymnasts.map(gymnast => (
                  <button
                    type="button"
                    key={gymnast.id}
                    className="gymnast-row gymnast-row--archived"
                    onClick={() => handleRowClick(gymnast)}
                  >
                    <span className="gymnast-row__name">{gymnast.firstName} {gymnast.lastName}</span>
                    <span className="gymnast-row__meta">
                      <span className="badge badge-secondary">Archived</span>
                      {levelPill(gymnast)}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Show message if no gymnasts found */}
          {activeGymnasts.length === 0 && (!showArchived || archivedGymnasts.length === 0) && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">No Gymnasts Found</h3>
              </div>
              <div>
                <p>No gymnasts match your search criteria.</p>
                {(searchTerm || searchParams.get('level') || showSessionOnly) && (
                  <div style={{ marginTop: '1rem' }}>
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={() => {
                        setSearchTerm('');
                        setSelectedSessionId(null);
                        setSessionGymnasts(new Set());
                        localStorage.removeItem('gymnastSessionFilter');
                        setSearchParams(new URLSearchParams());
                      }}
                    >
                      Clear All Filters
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Gymnasts; 